import { Injectable } from '@angular/core';
import {
  WISHLIST_PERSISTENT_MEMORY_DB_NAME,
  WISHLIST_PERSISTENT_MEMORY_STORE_NAME,
} from '../constants/wishlist-persistent-memory.constants';
import type { WishlistPersistentMemoryStore } from '../types/wishlist-persistent-memory-store.interface';

@Injectable({ providedIn: 'root' })
export class WishlistIndexedDbPersistentMemoryStoreService implements WishlistPersistentMemoryStore {
  private databasePromise: Promise<IDBDatabase | null> | null = null;

  public async getItem(key: string): Promise<unknown | null> {
    return this.runRequest<unknown>('readonly', (store: IDBObjectStore): IDBRequest<unknown> => store.get(key));
  }

  public async setItem(key: string, value: unknown): Promise<void> {
    await this.runRequest<IDBValidKey>('readwrite', (store: IDBObjectStore): IDBRequest<IDBValidKey> =>
      store.put(value, key),
    );
  }

  public async removeItem(key: string): Promise<void> {
    await this.runRequest<undefined>('readwrite', (store: IDBObjectStore): IDBRequest<undefined> =>
      store.delete(key),
    );
  }

  private async runRequest<TResult>(
    mode: IDBTransactionMode,
    requestFactory: (store: IDBObjectStore) => IDBRequest<TResult>,
  ): Promise<TResult | null> {
    const database: IDBDatabase | null = await this.openDatabase();

    if (database === null) {
      return null;
    }

    return new Promise<TResult | null>((resolve: (value: TResult | null) => void): void => {
      const transaction: IDBTransaction = database.transaction(WISHLIST_PERSISTENT_MEMORY_STORE_NAME, mode);
      const store: IDBObjectStore = transaction.objectStore(WISHLIST_PERSISTENT_MEMORY_STORE_NAME);
      const request: IDBRequest<TResult> = requestFactory(store);

      if (mode === 'readonly') {
        request.onsuccess = (): void => resolve(request.result ?? null);
        request.onerror = (): void => resolve(null);
        transaction.onabort = (): void => resolve(null);
        return;
      }

      transaction.oncomplete = (): void => resolve(null);
      transaction.onerror = (): void => resolve(null);
      transaction.onabort = (): void => resolve(null);
    });
  }

  private openDatabase(): Promise<IDBDatabase | null> {
    if (this.databasePromise !== null) {
      return this.databasePromise;
    }

    if (typeof indexedDB === 'undefined') {
      this.databasePromise = Promise.resolve(null);
      return this.databasePromise;
    }

    this.databasePromise = new Promise<IDBDatabase | null>((resolve: (database: IDBDatabase | null) => void): void => {
      const request: IDBOpenDBRequest = indexedDB.open(WISHLIST_PERSISTENT_MEMORY_DB_NAME, 1);

      request.onupgradeneeded = (): void => {
        const database: IDBDatabase = request.result;

        if (!database.objectStoreNames.contains(WISHLIST_PERSISTENT_MEMORY_STORE_NAME)) {
          database.createObjectStore(WISHLIST_PERSISTENT_MEMORY_STORE_NAME);
        }
      };
      request.onsuccess = (): void => resolve(request.result);
      request.onerror = (): void => resolve(null);
    });

    return this.databasePromise;
  }
}
